import { useState } from 'react';
import { Calendar } from '../icons';
import { formatLongDate, formatShortFieldDate } from './dateGrid';
import { DatePickerPopover } from './DatePickerPopover';

/**
 * Closed-state date field + popover trigger (first-run-setup.md SS5.2).
 * Styled identically to the shared text-input field chrome so the form
 * reads as one coherent field list until interacted with.
 */
export function DatePickerField({
  value,
  today,
  isDefaultValue,
  onChange,
}: {
  readonly value: string;
  readonly today: string;
  /** True while the value is still the untouched default — shows the
   *  "defaults to 30 days out" helper; disappears once the user picks
   *  explicitly (SS5.2). */
  readonly isDefaultValue: boolean;
  readonly onChange: (date: string) => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div style={{ position: 'relative' }}>
      <button
        type="button"
        role="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        // Explicit label (unambiguous full date, matching the popover
        // cells' own aria-label convention) — without this, the button's
        // accessible name falls back to concatenating the wrapping <label>
        // element's text plus the sibling helper paragraph below, which
        // reads as garbled ("Target date Defaults to 30 days out...")
        // rather than announcing the selected date at all.
        aria-label={formatLongDate(value)}
        onClick={() => setOpen(true)}
        className="num"
        style={{
          width: '100%',
          minHeight: 'var(--touch-target-min)',
          padding: '0 var(--space-3)',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--border-subtle)',
          background: 'var(--surface-sunken)',
          color: 'var(--text-primary)',
          fontSize: 'var(--type-body-size)',
          fontFamily: 'var(--font-family)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <span>{formatShortFieldDate(value)}</span>
        <Calendar size={20} color="var(--text-muted)" aria-hidden="true" />
      </button>

      {isDefaultValue && (
        <p
          style={{
            fontSize: 'var(--type-caption-size)',
            color: 'var(--text-muted)',
            marginTop: 'var(--space-1)',
          }}
        >
          Defaults to 30 days out — tap to change.
        </p>
      )}

      {open && (
        <DatePickerPopover
          selected={value}
          today={today}
          onSelect={onChange}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}
